/**
 * SOVRANOVA — useWakeWord
 *
 * Hands-free wake-word detection ("SOVRANOVA") using the browser's Web Speech
 * API. Runs only while started (after a user gesture grants the mic), and
 * yields the mic the moment a live conversation takes over.
 *
 * Small interface (supported / listening / error / start / stop) so an
 * on-device engine like Picovoice Porcupine — fully private, cross-browser —
 * can replace the internals later without touching the companion.
 */
import { useRef, useState, useCallback, useEffect } from 'react'

const SR = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null

// Wake word is "Companion" (2026-08-13, David's request — friendlier to say
// aloud than the brand name). "SOVRANOVA" still works as a fallback so nobody
// used to the old word is stranded. Both include common speech-to-text
// mishearings.
function isWake(transcript) {
  const t = (transcript || '').toLowerCase().replace(/[^a-z ]/g, '')
  if (/\bcompanion\b/.test(t)) return true
  if (/\bcompanions?\b|\bcompanionn?\b/.test(t)) return true
  if (t.replace(/\s/g, '').includes('sovranova')) return true
  return /\bsov\w*\s*nova\b/.test(t)
}

export function useWakeWord({ onWake } = {}) {
  const recRef      = useRef(null)
  const onWakeRef   = useRef(onWake)
  const stoppingRef = useRef(false)
  const [listening, setListening] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => { onWakeRef.current = onWake }, [onWake])

  const stop = useCallback(() => {
    stoppingRef.current = true
    const rec = recRef.current
    recRef.current = null
    try { rec?.stop() } catch (_) {}
    setListening(false)
  }, [])

  const start = useCallback(() => {
    if (!SR || recRef.current) return
    stoppingRef.current = false
    let rec
    try { rec = new SR() } catch (_) { setError('unsupported'); return }
    rec.continuous = true
    rec.interimResults = true
    rec.lang = 'en-US'
    rec.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (isWake(e.results[i][0]?.transcript)) { stop(); onWakeRef.current?.(); return }
      }
    }
    rec.onerror = (e) => {
      if (e?.error === 'not-allowed' || e?.error === 'service-not-allowed') { setError('blocked'); stop() }
    }
    rec.onend = () => {
      setListening(false)
      if (!stoppingRef.current && recRef.current === rec) {
        try { rec.start(); setListening(true) } catch (_) {}
      }
    }
    recRef.current = rec
    try { rec.start(); setListening(true); setError(null) }
    catch (_) { recRef.current = null }
  }, [stop])

  useEffect(() => () => stop(), [stop])

  return { supported: !!SR, listening, error, start, stop }
}
